import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { resolveImage, formatINR } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { cart, updateQty, removeItem } = useCart();
  const navigate = useNavigate();

  if (!cart.items.length) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="empty-cart">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-secondary rounded-full">
          <ShoppingBag className="w-8 h-8 text-muted-foreground" />
        </div>
        <h1 className="font-serif text-3xl text-obsidian mb-3">Your cart is empty</h1>
        <p className="text-muted-foreground mb-8">Looks like you haven't added any chains yet.</p>
        <Link to="/catalogue" className="inline-flex items-center gap-2 bg-obsidian text-white font-semibold px-8 py-3.5 hover:bg-gold hover:text-obsidian transition-colors rounded-sm" data-testid="cart-shop-btn">
          Start Shopping <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="cart-page">
      <h1 className="font-serif text-4xl lg:text-5xl text-obsidian mb-8">Shopping Cart</h1>

      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-4">
          {cart.items.map((item) => (
            <div key={item.product_id} className="flex gap-4 bg-white border border-black/10 p-4" data-testid={`cart-item-${item.sku}`}>
              <Link to={`/product/${item.product_id}`} className="w-24 h-28 sm:w-28 sm:h-32 shrink-0 overflow-hidden bg-secondary">
                <img src={resolveImage(item.image)} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{item.sku}</p>
                    <Link to={`/product/${item.product_id}`}><h3 className="font-serif text-xl text-obsidian truncate hover:text-gold-dark transition-colors">{item.name}</h3></Link>
                  </div>
                  <button onClick={() => removeItem(item.product_id)} className="text-muted-foreground hover:text-discount transition-colors p-1 h-fit" data-testid={`remove-${item.sku}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-baseline gap-2 mt-2">
                  <span className="font-bold text-obsidian">{formatINR(item.price)}</span>
                  {item.discount_percent > 0 && <span className="text-xs text-muted-foreground line-through">{formatINR(item.mrp)}</span>}
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-black/20 rounded-sm">
                    <button onClick={() => updateQty(item.product_id, item.quantity - 1)} className="p-2 hover:bg-secondary transition-colors" data-testid={`cart-minus-${item.sku}`}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-9 text-center text-sm font-semibold" data-testid={`cart-qty-${item.sku}`}>{item.quantity}</span>
                    <button onClick={() => updateQty(item.product_id, item.quantity + 1)} className="p-2 hover:bg-secondary transition-colors" data-testid={`cart-plus-${item.sku}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="font-bold text-obsidian" data-testid={`cart-line-${item.sku}`}>{formatINR(item.line_total)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 h-fit bg-white border border-black/10 p-6" data-testid="cart-summary">
          <h2 className="font-serif text-2xl text-obsidian mb-5">Order Summary</h2>
          <div className="space-y-3 text-sm">
            <Row label="Total MRP" value={formatINR(cart.subtotal_mrp)} />
            <Row label="Discount" value={`- ${formatINR(cart.total_savings)}`} className="text-green-700" />
            <div className="border-t border-black/10 pt-3 flex justify-between items-baseline">
              <span className="font-semibold text-obsidian">Total</span>
              <span className="font-bold text-2xl text-obsidian" data-testid="cart-total">{formatINR(cart.total)}</span>
            </div>
          </div>
          {cart.total_savings > 0 && (
            <p className="text-xs text-green-700 bg-green-50 px-3 py-2 mt-4 rounded-sm" data-testid="cart-savings">
              You're saving {formatINR(cart.total_savings)} on this order!
            </p>
          )}
          <button onClick={() => navigate("/checkout")} className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-gold text-obsidian font-semibold py-3.5 hover:bg-gold-light transition-colors rounded-sm" data-testid="checkout-btn">
            Proceed to Checkout <ArrowRight className="w-4 h-4" />
          </button>
          <Link to="/catalogue" className="block text-center text-sm text-muted-foreground hover:text-obsidian mt-4 transition-colors">Continue Shopping</Link>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, className = "" }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-medium ${className}`}>{value}</span>
  </div>
);
